import React, { ReactNode, useState } from "react";
import { Popover } from "antd";

type TGenericPopoverProps = {
  content: ReactNode; // Content of the popover
  trigger?: "click" | "hover" | "focus"; // Popover trigger
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight"; // Placement of the popover
  icon?: JSX.Element; // FontAwesomeIcon to show on the trigger
  overlayClassName?: string; // Custom class for the overlay
  iconClassName?: string; // Custom class for the icon
  containerClassName?: string; // Custom class for the container div
  arrow?: boolean;
};

const GenericPopover: React.FC<TGenericPopoverProps> = ({
  content,
  trigger = "click",
  placement = "bottomRight",
  icon,
  overlayClassName = "",
  iconClassName = "",
  containerClassName = "",
  arrow = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Handle popover visibility toggle
  const handleVisibleChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  // Close the popover when clicking anywhere else
  const handleClickOutside = () => {
    setIsVisible(false);
  };

  // Add event listener to handle outside clicks
  React.useEffect(() => {
    if (isVisible) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isVisible]);

  return (
    <Popover
      visible={isVisible}
      onVisibleChange={handleVisibleChange}
      arrow={arrow}
      content={content}
      trigger={trigger}
      placement={placement}
      overlayClassName={overlayClassName}
    >
      <div
        className={containerClassName}
        onClick={(e) => {
          e.stopPropagation(); // Prevent the click from closing the popover
          setIsVisible(!isVisible); // Toggle visibility
        }}
      >
        {icon}
      </div>
    </Popover>
  );
};

export default GenericPopover;
